/**
 * Infinite Scroll Component for Collection Pages
 *
 * Uses IntersectionObserver to detect when the sentinel is near the viewport,
 * then fetches the next page via the Shopify Section Rendering API and appends
 * product cards to the existing product-list element.
 *
 * Preloads the next page immediately after each append so products are ready
 * by the time the user scrolls down.
 */

class AcdcInfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.nextUrl = this.dataset.nextUrl;
    this.sectionId = this.getAttribute('section-id');
    this.listSelector = this.getAttribute('list-selector') || 'product-list';
    this.abortController = null;
    this.isLoading = false;
    this.prefetchedDoc = null;
    this.prefetchUrl = null;

    // Per-card reveal observer — each card fades in individually on scroll
    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('acdc-reveal--visible');
            this.revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (!this.nextUrl) return;

    this.spinner = this.querySelector('.infinite-scroll__spinner');
    this.status = this.querySelector('.infinite-scroll__status');
    this.loadMoreBtn = this.querySelector('.infinite-scroll__load-more');

    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', () => {
        this.removeAttribute('error');
        this.loadNextPage();
      });
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isLoading) {
            this.loadNextPage();
          }
        });
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    this.observer.observe(this);

    // Start preloading the next page immediately
    this.prefetch(this.nextUrl);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.revealObserver) {
      this.revealObserver.disconnect();
      this.revealObserver = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.prefetchedDoc = null;
    this.prefetchUrl = null;
  }

  /**
   * Prefetch a page and store the parsed document for instant use later.
   */
  async prefetch(url) {
    if (!url) return;

    const separator = url.includes('?') ? '&' : '?';
    const fetchUrl = `${url}${separator}section_id=${this.sectionId}`;

    try {
      const response = await fetch(fetchUrl);
      if (!response.ok) return;

      const html = await response.text();
      // Only store if this component is still connected and the URL is still relevant
      if (this.isConnected && this.nextUrl === url) {
        this.prefetchedDoc = new DOMParser().parseFromString(html, 'text/html');
        this.prefetchUrl = url;
      }
    } catch {
      // Prefetch failures are silent — the main fetch will handle errors
    }
  }

  async loadNextPage() {
    if (this.isLoading || !this.nextUrl) return;

    this.isLoading = true;
    this.setAttribute('loading', '');
    this.removeAttribute('error');

    if (this.status) {
      this.status.textContent = 'Loading more products\u2026';
    }

    this.abortController = new AbortController();

    try {
      let doc;

      // Use prefetched document if available and matches current next URL
      if (this.prefetchedDoc && this.prefetchUrl === this.nextUrl) {
        doc = this.prefetchedDoc;
        this.prefetchedDoc = null;
        this.prefetchUrl = null;
      } else {
        const separator = this.nextUrl.includes('?') ? '&' : '?';
        const fetchUrl = `${this.nextUrl}${separator}section_id=${this.sectionId}`;

        const response = await fetch(fetchUrl, {
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const html = await response.text();
        doc = new DOMParser().parseFromString(html, 'text/html');
      }

      // Extract new items from the fetched page
      const fetchedList = doc.querySelector(this.listSelector);
      const currentList = document.querySelector(
        `#shopify-section-${this.sectionId} ${this.listSelector}`
      );

      if (fetchedList && currentList) {
        // Clear theme's reveal-on-scroll from all cards to prevent conflicts
        currentList.querySelectorAll('[reveal-on-scroll="true"]').forEach((el) => {
          el.removeAttribute('reveal-on-scroll');
        });

        Array.from(fetchedList.children).forEach((child) => {
          const imported = document.importNode(child, true);

          // Strip theme reveal and apply our own per-card reveal class
          imported.removeAttribute('reveal-on-scroll');
          imported.classList.add('acdc-reveal');

          currentList.appendChild(imported);

          // Observe each card individually so they pew-pew-pew in on scroll
          if (this.revealObserver) {
            this.revealObserver.observe(imported);
          }
        });
      }

      // Check if there's a next page in the fetched content
      const fetchedSentinel = doc.querySelector('acdc-infinite-scroll');

      if (fetchedSentinel && fetchedSentinel.dataset.nextUrl) {
        this.nextUrl = fetchedSentinel.dataset.nextUrl;

        // Preload the next page so it's ready when the user scrolls
        this.prefetch(this.nextUrl);
      } else {
        this.nextUrl = null;

        if (this.observer) {
          this.observer.disconnect();
        }
      }

      if (this.status) {
        this.status.textContent = '';
      }
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error('Infinite scroll fetch failed:', error);
      this.setAttribute('error', '');

      if (this.status) {
        this.status.textContent =
          'Failed to load products. Click the button below to try again.';
      }
    } finally {
      this.isLoading = false;
      this.removeAttribute('loading');
      this.abortController = null;
    }
  }
}

if (!window.customElements.get('acdc-infinite-scroll')) {
  window.customElements.define('acdc-infinite-scroll', AcdcInfiniteScroll);
}
